import { motion } from "framer-motion";
import { WHATSAPP_HREF } from "@/lib/links";
import { trackWhatsAppClick } from "@/lib/analytics";

const HERO_IMG = "/images/hero.jpg";

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                {/* Top meta row */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease }}
                    className="flex flex-wrap items-center justify-between gap-4 mb-12 lg:mb-20 text-xs uppercase tracking-[0.22em] font-bold text-black/60"
                >
                    <span data-testid="hero-meta-position">
                        / Modern Creative Studio
                    </span>
                    <span>Mumbai · India ⇄ Worldwide</span>
                </motion.div>

                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10 items-start">
                    {/* Headline */}
                    <div className="col-span-12 lg:col-span-8">
                        <h1
                            data-testid="hero-heading"
                            className="font-display font-black uppercase leading-[0.86] tracking-[-0.04em] text-[16vw] sm:text-[14vw] lg:text-[11.5vw]"
                        >
                            <motion.span
                                initial={{ opacity: 0, y: 60 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease, delay: 0.05 }}
                                className="block"
                            >
                                Digital
                            </motion.span>
                            <motion.span
                                initial={{ opacity: 0, y: 60 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease, delay: 0.18 }}
                                className="block outline-text"
                            >
                                beej<span className="text-olive">·</span>
                            </motion.span>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease, delay: 0.45 }}
                            className="mt-8 font-serif italic text-3xl sm:text-4xl lg:text-5xl text-black/75"
                        >
                            From seed to scale.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease, delay: 0.6 }}
                            className="mt-10 max-w-xl text-base lg:text-lg text-black/70 leading-relaxed"
                        >
                            Building brands from seed to scale through
                            strategy, design and measurable growth.
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease, delay: 0.75 }}
                            className="mt-10 flex flex-wrap items-center gap-4"
                        >
                            <a
                                data-testid="hero-cta-whatsapp"
                                href={WHATSAPP_HREF}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    trackWhatsAppClick({
                                        section: "hero",
                                        buttonName: "hero_whatsapp",
                                    })
                                }
                                className="group inline-flex items-center gap-3 bg-obsidian text-bone px-7 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive hover:text-obsidian transition-colors duration-300"
                            >
                                <span
                                    aria-hidden
                                    className="inline-block w-2 h-2 bg-olive-mint rounded-full"
                                />
                                Chat on WhatsApp
                                <span
                                    aria-hidden
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </a>
                            <a
                                data-testid="hero-cta-discovery"
                                href="#contact"
                                className="inline-flex items-center gap-2 px-2 py-4 text-xs uppercase tracking-[0.2em] font-bold link-underline"
                            >
                                Book a discovery call
                            </a>
                        </motion.div>
                    </div>

                    {/* Side pillar image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, ease, delay: 0.3 }}
                        className="col-span-12 lg:col-span-4 relative"
                    >
                        <div className="relative overflow-hidden aspect-[3/4] lg:aspect-[2/3.2]">
                            <img
                                src={HERO_IMG}
                                alt="Creative team collaborating around a table with laptops, notebooks and colour references — Digitalbeej strategy in action"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                            />
                            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-bone text-[10px] uppercase tracking-[0.2em] font-bold mix-blend-difference">
                                <span>Mumbai · 19.07°N</span>
                                <span>FIG. 01</span>
                            </div>
                        </div>

                        {/* Signals strip */}
                        <div className="mt-6 grid grid-cols-3 border-t border-black/15">
                            {[
                                { k: "Founder", v: "led studio" },
                                { k: "Senior", v: "hands only" },
                                { k: "From", v: "seed to scale" },
                            ].map((s) => (
                                <div
                                    key={s.v}
                                    className="py-5 pr-3 border-r last:border-r-0 border-black/15"
                                >
                                    <div className="font-display font-bold text-lg lg:text-xl tracking-tight">
                                        {s.k}
                                    </div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-black/55 font-bold">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
